import React, { useState, useMemo } from 'react';
import Text, { TEXT_VARIANTS } from 'ui/Typography/Text';
import { debounce } from 'lodash';
import SessionListing from 'components/InPersonTraining/Attending/Registration/SessionListing';
import AttendanceStatus from 'components/InPersonTraining/EventRegistrationOverview/AttendanceStatus';
import AttendanceForm from 'components/InPersonTraining/Attending/Registration/AttendanceForm';
import request from 'utils/request_ajax';
import { isEmpty } from 'lodash/fp';
import { LESSON_EVENT, routes } from 'constants/config';
import classNames from 'classnames';
import { AlertTriangle } from 'react-feather';
import moment, { ISO_8601, now } from 'moment';

const LessonEventCard = ({
  event,
  sessions,
  attendance,
  canChangeAttendance,
  type,
  setNewAttendance,
  previewing,
  userId,
  parentEventAttendance,
  selectedSessionId,
  setSelectedSessionId,
}) => {
  /* eslint-disable camelcase */
  /*
  Fix snake_case issues. Once all of InPersonTraining/Training
  sends camelCase including in child components, then we can remove this.
  */

  const isPreview = previewing;
  const lessonEventPreview = classNames('mb-12', {
    'disabled pe-none': isPreview,
  });
  const isPostiveAttendance = ['did_attend', 'will_attend'].includes(
    attendance?.status,
  );

  const hasMultipleSessions = type.multi_session;
  // Single session selectedSessionId starts as only available session id
  // Multi session selectedSessionId starts as default null value
  function getSelectedSessionId() {
    return !hasMultipleSessions ? sessions[0].id : null;
  }

  const [isModifyingRSVP, setIsModifyingRSVP] = useState(false);
  const [registrationSubmitting, setRegistrationSubmitting] = useState(false);
  const [notAttendingSubmitting, setNotAttendingSubmitting] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const desc = event.description;
  const { past_event } = type;
  const truncatedDesc = `${desc.substring(0, 210)}...`;
  const isLongDescription = desc.length > 210;

  function currentSession() {
    const applicableSession = sessions.find(
      session => session.id === attendance.session_id,
    );
    if (applicableSession) {
      return applicableSession;
    }
    return sessions[0];
  }

  // User has registered for an event as did/will or did_not/will_not attend, OR
  // User has no attendance record for a past event
  function hasRsvpInfo() {
    return (
      (!isEmpty(attendance) &&
        (attendance.did_attend !== null || attendance.rsvp_status !== null) &&
        !isModifyingRSVP) ||
      (isEmpty(attendance) && past_event)
    );
  }

  function isPastEvent() {
    return past_event;
  }

  // User was invited, did not respond, and is unable to change their attendance for the now-past event
  function pastEventUneditable() {
    return past_event && !canChangeAttendance;
  }

  // event has no registration capacity set on any sessions
  function noRegistrationCapacity() {
    if (hasMultipleSessions) {
      return sessions.every(session => session?.max_attendees === null);
    }
    return !hasMultipleSessions && sessions[0]?.max_attendees === null;
  }

  // Sessions all have a max attendance set and all sessions are at capacity
  function eventAtCapacity() {
    if (noRegistrationCapacity()) return false;

    if (hasMultipleSessions) {
      if (event.rsvp_type === 'all') {
        return sessions.some(session => session.type.at_capacity);
      }
      return sessions.every(session => session.type.at_capacity);
    }
    return (
      !hasMultipleSessions &&
      sessions[0]?.total_registered >= sessions[0]?.max_attendees
    );
  }

  function previewEventAtCapacity() {
    if (noRegistrationCapacity()) return false;

    if (hasMultipleSessions) {
      if (event.rsvp_type === 'all') {
        return sessions.some(
          session => session.total_registered >= session.max_attendees,
        );
      }
      return sessions.every(
        session => session.total_registered >= session.max_attendees,
      );
    }
    return (
      !hasMultipleSessions &&
      sessions[0]?.total_registered >= sessions[0]?.max_attendees
    );
  }

  function canChangeRSVP() {
    return (
      !type.past_event ||
      canChangeAttendance ||
      (!type.pastEvent && (!eventAtCapacity() || isPostiveAttendance))
    );
  }

  function futureSessionsAtCapacity() {
    const futureSessions = sessions.filter(session =>
      moment(session.start_time, ISO_8601).isAfter(now()),
    );
    return futureSessions.every(session => session.type.at_capacity);
  }

  function showSessionAtCapacityNotice() {
    if (noRegistrationCapacity()) return false;

    if (isPreview) {
      // preview lesson for single-session event at capacity will display At Capacity banner
      // and will not display banner otherwise
      if (!hasMultipleSessions && eventAtCapacity()) return true;
      if (!hasMultipleSessions && !eventAtCapacity()) return false;

      // in a company that allows learners to change past attendance,
      // preview lesson for a multi-session event at capacity
      // will display At Capacity banner
      if (hasMultipleSessions) {
        if (canChangeAttendance && eventAtCapacity()) return true;

        // in a company that does NOT allow learners to change past attendance,
        // preview lesson for
        // a multi-session event fully at capacity OR
        // a multi-session event with future sessions at capacity
        // will display At Capacity banner
        if (!canChangeAttendance) {
          if (eventAtCapacity()) return true;
          if (!isPastEvent() && futureSessionsAtCapacity()) return true;
        }
      }
    }

    // if user has not registered at all AND
    // event is entirely full, then
    // show At Capacity
    if (isEmpty(attendance) && eventAtCapacity()) return true;

    // if user has not registered at all AND
    // event sessions are available AND
    // if multiple sessions AND
    // if user cannot change past attendance AND
    // if future event sessions are full
    // then show At Capacity
    if (
      isEmpty(attendance) &&
      !eventAtCapacity() &&
      hasMultipleSessions &&
      !canChangeAttendance &&
      !past_event &&
      futureSessionsAtCapacity()
    ) {
      return true;
    }

    // if user has already registered as will/did attend, then
    // do not show At Capacity
    if (
      !isEmpty(attendance) &&
      (attendance.did_attend ||
        attendance.rsvp_status === LESSON_EVENT.RSVP_STATUS)
    ) {
      return false;
    }

    // if user is currently registered as did/will NOT attend AND
    if (
      !isEmpty(attendance) &&
      !(
        attendance.did_attend ||
        attendance.rsvp_status === LESSON_EVENT.RSVP_STATUS
      )
    ) {
      // if single session event and event is entirely full,
      // then show At Capacity
      if (!hasMultipleSessions && eventAtCapacity()) {
        return true;
      }

      // if multiple sessions AND
      if (hasMultipleSessions) {
        // if user can change past attendance AND
        // if all future/past event sessions are full,
        // then show At Capacity
        if (canChangeAttendance && eventAtCapacity()) {
          return true;
        }

        // if user cannot change past attendance AND
        if (!canChangeAttendance) {
          // if future event sessions are full
          // then show At Capacity
          if (!past_event && futureSessionsAtCapacity()) {
            return true;
          }

          // if past event sessions are full, then
          // show At Capacity
          if (past_event && eventAtCapacity()) {
            return true;
          }
        }
      }
    }

    // default to not show At Capacity banner
    return false;
  }

  function showAttendanceStatus() {
    if (isPreview && showSessionAtCapacityNotice()) return true;
    if (isPreview && !showSessionAtCapacityNotice()) return false;

    if (isPastEvent()) return true;

    // if user has not registered at all for future event that is not at capacity, do not show a status
    if (isEmpty(attendance) && !isPastEvent() && !showSessionAtCapacityNotice())
      return false;

    // if user has not registered at all for an event that is not at capacity,
    // and for which they would be able to change past attendance, do not show a status
    if (canChangeAttendance && isEmpty(attendance) && !eventAtCapacity())
      return false;

    return (
      hasRsvpInfo() ||
      pastEventUneditable() ||
      eventAtCapacity() ||
      (!isPastEvent() && futureSessionsAtCapacity())
    );
  }

  function showAttendanceControls() {
    if (isPreview && previewEventAtCapacity()) return false;
    if (isPostiveAttendance && !(isPastEvent() && !canChangeAttendance))
      return true;
    if (event.rsvp_type === 'all' && eventAtCapacity()) return false;
    if (canChangeAttendance && !showSessionAtCapacityNotice()) return true;

    return !isPastEvent() && !futureSessionsAtCapacity();
  }

  function showAddToCalendar() {
    return (
      !type.past_event &&
      !isEmpty(attendance) &&
      attendance.rsvp_status === LESSON_EVENT.RSVP_STATUS
    );
  }

  const isArchived = () => event.archived_at != null;

  function isDisabled() {
    return (
      isPreview ||
      showSessionAtCapacityNotice() ||
      (isPastEvent() && !canChangeAttendance && isEmpty(attendance))
    );
  }

  const debouncedUpdateAttendance = useMemo(() => {
    return debounce(function handleUpdateAttendance(attendanceStatus) {
      const setAttendanceUrl =
        isPastEvent() || event.rsvp_type === 'single'
          ? // To Cleanup: If user clicks "I will not attend" but session hasn't been selected,
            // need to retrieve a valid session id to complete request to new API
            routes.TRAINING.SESSION_SET_ATTENDANCE(
              event.id,
              selectedSessionId || sessions[0].id,
            )
          : routes.TRAINING.EVENT_SET_RSVP(event.id);

      const status = isPastEvent()
        ? {
            attendanceStatus: attendanceStatus
              ? 'did_attend'
              : 'did_not_attend',
          }
        : { rsvpStatus: attendanceStatus ? 'will_attend' : 'will_not_attend' };

      request
        .putJSON(setAttendanceUrl, {
          ...status,
          userId,
          createdFrom: null,
        })
        .then(response => {
          if (response.data) {
            setNewAttendance(prev => !prev);
          }
        });
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    event.id,
    getSelectedSessionId,
    selectedSessionId,
    setNewAttendance,
    event.rsvp_type,
    sessions,
    userId,
  ]);

  return (
    <div>
      {isArchived() && (
        <div className="alert alert-warning">
          <AlertTriangle size={18} className="mr-4" />
          <Text variant={TEXT_VARIANTS.BODY}>
            {t('learning.lessons.event.archived_event_message')}
          </Text>
        </div>
      )}
      <div
        className={`LessonEventCard--content d-f fd-c jc-c ${
          isArchived() ? 'LessonEventCard--disabled' : ''
        }`}
      >
        <div className="LessonEventCard--header d-f fd-c">
          <Text variant={TEXT_VARIANTS.SUBHEADER_BOLD} className="mb-12">
            {event.name}
          </Text>
          {event.description && (
            <Text variant={TEXT_VARIANTS.BODY_LIGHT} className="mb-12">
              {!isLongDescription || showFullDesc ? desc : truncatedDesc}
              {isLongDescription && (
                <button
                  aria-label={showFullDesc ? 'see-less-btn' : 'see-more-btn'}
                  type="button"
                  className="u-resetBtn c-link as-fe ml-4"
                  onClick={() => setShowFullDesc(prev => !prev)}
                >
                  {showFullDesc
                    ? t('learning.lessons.event.see_less_button')
                    : t('learning.lessons.event.see_more_button')}
                </button>
              )}
            </Text>
          )}
          <div className="LessonEventCard SessionDetailCell pt-16">
            <SessionListing
              sessions={sessions}
              selectedSessionId={selectedSessionId}
              handleSessionClick={selectedId =>
                setSelectedSessionId(selectedId)
              }
              hasMultipleSessions={hasMultipleSessions}
              attendance={attendance}
              currentSession={currentSession()}
              event={event}
              changingAttendance={isModifyingRSVP}
              previewing={isDisabled()}
              isArchived={isArchived()}
              canChangeAttendance={canChangeAttendance}
              pastEvent={isPastEvent()}
              alwaysShowMeetingUrl
            />
          </div>
          {showAttendanceStatus() && (
            <div className="mt-16">
              <AttendanceStatus
                className="LessonEventCard EventRegistration-AttendanceStatus"
                attendance={attendance}
                pastEvent={isPastEvent()}
                showSessionAtCapacityNotice={showSessionAtCapacityNotice()}
              />
            </div>
          )}
        </div>
      </div>
      {showAttendanceControls() && !isArchived() && (
        <div
          className={`LessonEventCard--controls d-f ai-c pl-24 ${lessonEventPreview}`}
        >
          <AttendanceForm
            showRsvpButtons={() =>
              isModifyingRSVP || !hasRsvpInfo() || isPreview
            }
            pastEvent={isPastEvent()}
            handleUpdateAttendance={isAttending => {
              if (isAttending) {
                setRegistrationSubmitting(true);
              } else {
                setNotAttendingSubmitting(true);
              }
              debouncedUpdateAttendance(isAttending);
            }}
            handleChangeAttendance={() => setIsModifyingRSVP(true)}
            canChangeAttendance={!isPreview && canChangeRSVP()}
            eventAtCapacity={eventAtCapacity()}
            showAddToCalendar={showAddToCalendar}
            event={event}
            session={currentSession()}
            selectedSessionId={selectedSessionId}
            attendance={attendance}
            registrationSubmitting={registrationSubmitting}
            notAttendingSubmitting={notAttendingSubmitting}
            showJoinMeetingButton={false}
            previewing={isPreview}
            hasMultipleSessions={hasMultipleSessions}
            isPostiveAttendance={isPostiveAttendance}
          />
        </div>
      )}
    </div>
  );
};

export default LessonEventCard;
